import React from 'react';
import classNames from 'classnames';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import MarkdownView from 'components/markup/MarkdownView/MarkdownView';
import { TODO_BODY_FEATURES } from 'constants/todos';
import { toMarkdownString } from 'utils/markdown/markdown';

function CollapsibleTodoBody({ body, className, compact }) {
  const [open, setOpen] = React.useState(false);
  const contentId = React.useId();
  const contentRef = React.useRef(null);
  const bodySource = toMarkdownString(body).replace(/\r\n/g, '\n').trim();
  const sourceRows = bodySource.split('\n');
  const isExpandable = sourceRows.length > 1;
  const collapsedHeight = compact ? 30 : 36;
  const controlOffset = compact ? 22 : 24;
  const [expandedHeight, setExpandedHeight] = React.useState(collapsedHeight + controlOffset);

  React.useLayoutEffect(() => {
    if (!isExpandable || !contentRef.current) {
      return undefined;
    }

    const element = contentRef.current;

    function measureHeight() {
      setExpandedHeight(element.offsetHeight + controlOffset);
    }

    measureHeight();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measureHeight);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [body, compact, controlOffset, isExpandable]);

  function toggleOpen() {
    setOpen((current) => !current);
  }

  function handleKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    toggleOpen();
  }

  if (!isExpandable) {
    return (
      <MarkdownView
        markdown={body}
        features={TODO_BODY_FEATURES}
        className={className}
        compact={compact}
      />
    );
  }

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={0}
        aria-controls={contentId}
        aria-expanded={open}
        aria-label={open ? 'Collapse todo body' : 'Expand todo body'}
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
        className="group relative block w-full cursor-pointer rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60"
      >
        <div
          id={contentId}
          className="w-full overflow-hidden transition-[height] duration-300 ease-out will-change-[height]"
          style={{ height: open ? expandedHeight : collapsedHeight }}
        >
          <div ref={contentRef} className={open ? 'pb-6' : ''}>
            {!open && (
              <div data-testid="todo-body-preview" className="relative w-full overflow-hidden">
                <MarkdownView
                  markdown={body}
                  features={TODO_BODY_FEATURES}
                  compact={compact}
                  className="text-gray-400 transition-colors duration-300"
                />
              </div>
            )}
            {open && (
              <MarkdownView
                markdown={body}
                features={TODO_BODY_FEATURES}
                compact={compact}
                className="text-gray-300 transition-colors duration-300"
              />
            )}
          </div>
        </div>
        <span
          data-testid="todo-body-fade"
          aria-hidden="true"
          className={classNames(
            'pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-b from-gray-800/0 via-gray-800/85 to-gray-800 transition-opacity duration-300 ease-out',
            compact ? 'h-6' : 'h-7',
            open ? 'opacity-0' : 'opacity-100'
          )}
        />
        <ChevronDownIcon
          data-testid="todo-body-toggle-icon"
          aria-hidden="true"
          className={classNames(
            'pointer-events-none absolute left-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 text-gray-500 transition-all duration-300 ease-out',
            compact ? 'bottom-0.5' : 'bottom-1',
            open ? 'rotate-180 text-gray-400' : ''
          )}
        />
      </div>
    </div>
  );
}

export default function TodoBodyMarkup({ body, className = '', compact = false, collapsible = false }) {
  if (!body) {
    return null;
  }

  if (collapsible) {
    return <CollapsibleTodoBody body={body} className={className} compact={compact} />;
  }

  return (
    <MarkdownView
      markdown={body}
      features={TODO_BODY_FEATURES}
      className={className}
      compact={compact}
    />
  );
}
